import React from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import { LICENCE_KEY } from "../constants/charts";
import { functions } from "../constants/common-functions";

am4core.addLicense(LICENCE_KEY);

export default function AmChartDoughnutIndicator({
  id,
  percentage,
  label,
  width,
  height,
  onClick,
  color,
}) {
  const containerRef = React.useRef(null);
  const chartRef = React.useRef(null);
  const seriesRef = React.useRef(null);

  // Create once
  React.useEffect(() => {
    if (!containerRef.current) return;

    const chart = am4core.create(containerRef.current, am4charts.PieChart);
    chartRef.current = chart;

    chart.radius = am4core.percent(100);
    chart.innerRadius = am4core.percent(85);
    chart.fontSize = 12;
    chart.padding(0, 0, 0, 0);

    // Entrance animation (appear) settings
    chart.defaultState.transitionDuration = 600; // chart container fade-in
    chart.hiddenState.properties.opacity = 0;

    const series = chart.series.push(new am4charts.PieSeries());
    seriesRef.current = series;

    series.dataFields.value = "value";
    series.dataFields.category = "label";

    // Smooth updates between data changes
    series.interpolationDuration = 600;       // tween time for value changes
    series.interpolationEasing = am4core.ease.quadOut;
    series.sequencedInterpolation = true;     // stagger the slices a touch
    series.sequencedInterpolationDelay = 60;

    // Initial "appear" animation
    series.hiddenState.properties.endAngle = -90; // sweep in
    series.hiddenState.properties.startAngle = -90;

    series.labels.template.disabled = true;
    series.slices.template.strokeWidth = 0;
    series.slices.template.fillOpacity = 0.9;
    series.slices.template.propertyFields.fill = "color";
    
    // Inside your chart init effect
		series.interpolationDuration = 600;       // animate updates
		series.interpolationEasing = am4core.ease.cubicOut;
		series.sequencedInterpolation = false;
		
		// First-time appear
		series.appear(600, 60); // duration, delay
		chart.appear(600, 0);

    // Optional: subtle hover pop (clients love a little flourish)
    const hoverState = series.slices.template.states.getKey("hover");
    hoverState.properties.scale = 1.03;

    // Click handler (set once)
    series.slices.template.events.on("hit", (ev) => {
      const cat = ev?.target?.dataItem?.dataContext?.label;
      if (cat && onClick) onClick(cat);
    });

    // Kick the appear animation
    series.appear(600, 60);
    chart.appear(600, 0);

    return () => {
      chart.dispose();
      chartRef.current = null;
      seriesRef.current = null;
    };
  }, []);

  // Update data only (keeps instance; animates values smoothly)
  React.useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    chart.data = [
      { label: "live", value: percentage, color },
      { label: "grey", value: 100 - percentage, color: "rgba(100,100,100,0.4)" },
    ];

    // Triggers interpolation between old/new values (no remount)
    chart.invalidateRawData();
  }, [percentage, color]);

  // Styles
  const wrapperStyle = {
    position: "relative",
    minHeight: height,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-around",
    overflow: "hidden",
  };

  const chartBoxStyle = {
    width: width ? `${width}px` : "100%",
    minHeight: height ? `${height - 40}px` : "200px",
  };

  const labelStyle = {
    width: "100%",
    height: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-around",
    color: "black",
    fontSize: 14,
    marginTop:"-10px"
  };

  const overlayPctStyle = {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 32,
    pointerEvents: "none",
    color: "black",
    marginTop:"-5%"
  };

  return (
    <div style={wrapperStyle}>
      <div id={id} ref={containerRef} style={chartBoxStyle} />
      <div style={labelStyle}>{label}</div>
      <div style={overlayPctStyle}>{functions.formatNumber(percentage, 0)}%</div>
    </div>
  );
}
